/**
 * Viridis colormap — perceptually uniform, colorblind-friendly.
 * Control points sampled from matplotlib's viridis.
 */

const CONTROL_POINTS: [number, number, number, number][] = [
  // [t, R, G, B]
  [0.0, 68, 1, 84],
  [0.08, 71, 29, 110],
  [0.16, 64, 58, 131],
  [0.227, 60, 74, 134],
  [0.3, 52, 95, 141],
  [0.4, 40, 122, 142],
  [0.5, 31, 150, 139],
  [0.6, 53, 175, 122],
  [0.75, 149, 203, 69],
  [0.9, 237, 221, 39],
  [1.0, 253, 231, 37],
];

function lerp(a: number, b: number, f: number): number {
  return a + (b - a) * f;
}

export function viridisRgb(t: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(1, t));

  // Find the two control points to interpolate between
  for (let i = 0; i < CONTROL_POINTS.length - 1; i++) {
    const [t0, r0, g0, b0] = CONTROL_POINTS[i];
    const [t1, r1, g1, b1] = CONTROL_POINTS[i + 1];

    if (clamped >= t0 && clamped <= t1) {
      const f = (clamped - t0) / (t1 - t0);
      return [
        Math.round(lerp(r0, r1, f)),
        Math.round(lerp(g0, g1, f)),
        Math.round(lerp(b0, b1, f)),
      ];
    }
  }

  // Fallback to last point
  const last = CONTROL_POINTS[CONTROL_POINTS.length - 1];
  return [last[1], last[2], last[3]];
}

export function viridisColor(t: number): string {
  const [r, g, b] = viridisRgb(t);
  return `rgb(${r},${g},${b})`;
}

/** White text on dark cells (t≤0.65), black text on bright cells (t>0.65) */
export function viridisTextColor(t: number): string {
  return t <= 0.65 ? '#ffffff' : '#000000';
}

/** Normalize a raw value to [0,1] given global min/max */
export function normalizeValue(value: number, min: number, max: number): number {
  if (max === min) return 0.5;
  return (value - min) / (max - min);
}

/**
 * Returns [low, high] bounds at the given percentiles of the input values.
 * Uses Type-7 quantile (linear interpolation between adjacent ranks).
 * Non-finite values are filtered out. Empty input returns [0, 0]; single-value
 * or all-equal input returns [v, v].
 */
export function getPercentileBounds(values: number[], loP: number, hiP: number): [number, number] {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return [0, 0];
  if (finite.length === 1) return [finite[0], finite[0]];

  const sorted = finite.slice().sort((a, b) => a - b);
  const n = sorted.length;

  const quantile = (p: number): number => {
    const h = (n - 1) * p;
    const lo = Math.floor(h);
    const hi = Math.ceil(h);
    if (lo === hi) return sorted[lo];
    return sorted[lo] + (h - lo) * (sorted[hi] - sorted[lo]);
  };

  return [quantile(loP), quantile(hiP)];
}
